/**
 * Базовый компонент кнопки (вариант / размер / loading state).
 *
 * 30.04.2026 — варианты переведены на shell-токены и тематический градиент.
 */
import { forwardRef, ButtonHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';

const variants: Record<string, string> = {
  primary: 'shell-button focus:ring-shell-primary/30',
  secondary: 'shell-button-secondary focus:ring-shell-primary/20',
  danger: 'shell-button-danger focus:ring-red-300',
  ghost: 'shell-button-ghost focus:ring-shell-primary/20',
};

const sizes: Record<string, string> = {
  sm: 'h-8 px-3 text-sm rounded-xl',
  md: 'h-10 px-4 text-sm rounded-2xl',
  lg: 'h-12 px-5 text-base rounded-2xl',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  testId?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', isLoading, testId, children, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        data-testid={testId}
        className={cn(
          'inline-flex items-center justify-center gap-2 font-medium transition-all duration-200',
          'focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-shell-bg',
          'disabled:opacity-60 disabled:cursor-not-allowed',
          sizes[size],
          variants[variant],
          className,
        )}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
